import React, { useCallback, useEffect, useState } from "react";
import {
  Anchor,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconDownload } from "@tabler/icons-react";
import moment from "moment";
import { useApp } from "../../context/AppContext";
import { LoyaltyLocationSummaryResponse } from "../../types/loyalty";
import { formatCurrency } from "../../utils/cashDrawerUtils";

interface SummaryTabProps {
  businessLocationId: string;
  launchedDt?: string | null;
}

/** ISO yyyy-mm-dd for the backend query params. */
const toIso = (d: Date | null): string =>
  d ? moment(d).format("YYYY-MM-DD") : "";

/** Rolling-30-day window as the default range. */
const defaultRange = (): [Date, Date] => {
  const end = moment().startOf("day").toDate();
  const start = moment().subtract(30, "days").startOf("day").toDate();
  return [start, end];
};

const ZERO_SUMMARY: LoyaltyLocationSummaryResponse = {
  launched_dt: null,
  date_range: { start_date: "", end_date: "" },
  activity: {
    newly_enrolled_clients: { count: 0, total_clients_ever: 0 },
    earned_points: { points: 0, clients: 0 },
    redeemed_points: { points: 0, clients: 0 },
    added_points: { points: 0, clients: 0 },
    removed_points: { points: 0, clients: 0 },
  },
  liability: {
    unused_points: { points: 0, clients: 0 },
    unused_point_value: { amount: 0, clients: 0 },
  },
};

// Per 2026-08-26 feedback: the tile shows just the label + the primary
// number. Client counts / totals under the number were noise on this
// dashboard.
function MetricTile({
  label,
  primary,
  loading,
}: {
  label: string;
  primary: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Paper withBorder radius="md" p="lg" mih={140}>
      <Stack gap="xs" align="center" justify="center" h="100%">
        <Text size="sm" c="dimmed" ta="center">
          {label}
        </Text>
        {loading ? (
          <Skeleton height={28} width={110} radius="sm" />
        ) : (
          <Title order={2} ta="center" fw={700}>
            {primary}
          </Title>
        )}
      </Stack>
    </Paper>
  );
}

const formatNumber = (n: number) => n.toLocaleString("en-US");

/**
 * Rewards → Summary (loyalty FE1 §3.1, summary layout 2026-08-25).
 * Header + LAUNCHED pill + date-range picker over an Activity (5 tiles) and
 * Liability (2 tiles) grid. Reads GET /business-locations/{uuid}/loyalty/summary
 * (sibling backend PR #500); silent-degrades to zeros on failure with a
 * Refresh link.
 */
export default function SummaryTab({
  businessLocationId,
  launchedDt,
}: SummaryTabProps) {
  const { getLoyaltyLocationSummary } = useApp();

  const [range, setRange] = useState<[Date | null, Date | null]>(defaultRange);
  const [summary, setSummary] = useState<LoyaltyLocationSummaryResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  const [startDate, endDate] = range;

  const load = useCallback(async () => {
    if (!businessLocationId || !startDate || !endDate) return;
    setIsLoading(true);
    setHasFailed(false);
    try {
      const response = await getLoyaltyLocationSummary(businessLocationId, {
        start_date: toIso(startDate),
        end_date: toIso(endDate),
      });
      setSummary(response ?? ZERO_SUMMARY);
    } catch (error) {
      // Backend PR #500 may not be deployed yet — degrade to zeros so the
      // page stays useful and offer a manual retry.
      console.error("Error loading loyalty summary:", error);
      setSummary(ZERO_SUMMARY);
      setHasFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [businessLocationId, startDate, endDate, getLoyaltyLocationSummary]);

  useEffect(() => {
    load();
  }, [load]);

  const activity = summary?.activity ?? ZERO_SUMMARY.activity;
  const liability = summary?.liability ?? ZERO_SUMMARY.liability;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Stack gap={4}>
          <Title order={2}>Summary</Title>
          <Text size="sm" c="dimmed">
            View performance of your Loyalty program and keep track of unused
            point balances.
          </Text>
        </Stack>
        {/* Launched-date pill removed per 2026-08-26 feedback. */}
      </Group>

      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Title order={3}>Activity</Title>
        <Group gap="sm" wrap="wrap">
          <Tooltip label="Coming soon" withArrow>
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              disabled
              aria-label="Export activity"
            >
              Export
            </Button>
          </Tooltip>
          <DatePickerInput
            type="range"
            value={range}
            onChange={(next) => setRange(next as [Date | null, Date | null])}
            leftSection={<IconCalendar size={16} />}
            valueFormat="MMM D YYYY"
            aria-label="Activity date range"
            w={280}
            allowSingleDateInRange
          />
        </Group>
      </Group>

      {hasFailed && (
        <Text size="xs" c="dimmed">
          Couldn&apos;t load the latest data.{" "}
          <Anchor onClick={load} component="button" type="button" size="xs">
            Refresh
          </Anchor>
        </Text>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
        <MetricTile
          label="Newly enrolled clients"
          primary={formatNumber(activity.newly_enrolled_clients.count)}
          loading={isLoading && !summary}
        />
        <MetricTile
          label="Earned points"
          primary={formatNumber(activity.earned_points.points)}
          loading={isLoading && !summary}
        />
        <MetricTile
          label="Redeemed points"
          primary={formatNumber(activity.redeemed_points.points)}
          loading={isLoading && !summary}
        />
        <MetricTile
          label="Added points"
          primary={formatNumber(activity.added_points.points)}
          loading={isLoading && !summary}
        />
        <MetricTile
          label="Removed points"
          primary={formatNumber(activity.removed_points.points)}
          loading={isLoading && !summary}
        />
        {(isLoading || activity.expired_points) && (
          <MetricTile
            label="Expired points during this period"
            primary={
              activity.expired_points
                ? formatNumber(activity.expired_points.points)
                : "0"
            }
            loading={isLoading && !summary}
          />
        )}
      </SimpleGrid>

      <Divider mt="md" />

      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Title order={3}>Liability</Title>
        <Tooltip label="Coming soon" withArrow>
          <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            disabled
            aria-label="Export liability"
          >
            Export
          </Button>
        </Tooltip>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <MetricTile
          label="Unused points"
          primary={formatNumber(liability.unused_points.points)}
          loading={isLoading && !summary}
        />
        <MetricTile
          label="Unused point value"
          primary={formatCurrency(liability.unused_point_value.amount)}
          loading={isLoading && !summary}
        />
      </SimpleGrid>

      <Box />
    </Stack>
  );
}
